import { faSearch, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Input, Stack } from "@mui/joy";
import { type MRT_Row } from "material-react-table";
import { memo, useCallback } from "react";
import { EntitiesSwitch, ExportAsCsvButton } from "components";
import { type SelectedView } from "components/entities/dataRooms/DataRoomsList/models";
import { DataLabCreateButton } from "features/dataLabs";
import { type DataLab } from "features/dataLabs/models";
import { matchingIdTypeAndHashingAlgorithmPresentation } from "features/dataLabs/models";

interface DataLabsListToolbarProps {
  isTable: boolean;
  getRows?: () => MRT_Row<DataLab>[];
  handleSelectView: (view: SelectedView) => void;
  selectedView: SelectedView;
  searchValue: string;
  onSearchChange: (value: string) => void;
  hasData: boolean;
}

const getHeaders = () => [
  "Name",
  "Status",
  "Last modified",
  "Matching",
  "Matching ID",
  "Segments",
  "Demographics",
  "Embeddings",
];

const DataLabsListToolbar = memo<DataLabsListToolbarProps>(
  ({
    isTable,
    getRows,
    handleSelectView,
    selectedView,
    searchValue,
    onSearchChange,
    hasData,
  }) => {
    const isExportEnabled = isTable && hasData;
    const transformedRows = useCallback(() => {
      return getRows?.().map((row) => {
        const {
          name,
          isValidated,
          updatedAt,
          matchingIdFormat,
          matchingIdHashingAlgorithm,
          usersDataset,
          segmentsDataset,
          demographicsDataset,
          embeddingsDataset,
        } = row.original;
        const matchingId = matchingIdTypeAndHashingAlgorithmPresentation({
          matchingIdFormat,
          matchingIdHashingAlgorithm,
        });
        const formattedDate = updatedAt
          ? new Date(updatedAt).toLocaleString()
          : "-";
        return [
          name,
          isValidated ? "Validated" : "Not validated",
          formattedDate,
          usersDataset ? "Yes" : "No",
          matchingId,
          segmentsDataset ? "Yes" : "No",
          demographicsDataset ? "Yes" : "No",
          embeddingsDataset ? "Yes" : "No",
        ];
      });
    }, [getRows]);
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: isTable ? 2 : 3, py: isTable ? 2 : 3 }}
        width="100%"
      >
        <Stack direction="row">
          <EntitiesSwitch
            onChange={handleSelectView}
            selectedView={selectedView}
          />
          <Input
            endDecorator={
              searchValue ? (
                <IconButton onClick={() => onSearchChange("")}>
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              ) : null
            }
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search data labs"
            startDecorator={<FontAwesomeIcon icon={faSearch} />}
            sx={{ minWidth: "280px" }}
            value={searchValue}
          />
        </Stack>
        <Stack direction="row">
          {isExportEnabled ? (
            <ExportAsCsvButton
              fileName="data-labs.csv"
              getHeaders={getHeaders}
              getRows={transformedRows}
            />
          ) : null}
          <DataLabCreateButton />
        </Stack>
      </Stack>
    );
  }
);

export default DataLabsListToolbar;
